import moment from "moment-timezone";

/**
 * Copline's operational timezone. All shifts are defined in Eastern Time.
 * Every function takes an optional `timeZone` defaulting to this, so the same
 * code can render in another zone later without changes.
 */
export const COPLINE_TIMEZONE = "America/New_York";

/**
 * Builds an instant from a wall-clock date-time read in `timeZone`, returned as
 * an ISO-8601 string with offset (what we put on the wire).
 * `"2026-06-05 00:00"` in New York → `"2026-06-05T00:00:00-04:00"`.
 * @param {string} dateTime - e.g. "2026-06-05 00:00"
 * @param {string} [timeZone]
 * @returns {string} ISO-8601 with offset
 */
export const datetimeToInstant = (dateTime, timeZone = COPLINE_TIMEZONE) =>
  moment.tz(dateTime, timeZone).format();

/**
 * Builds a shift's `{ effectiveDatetime, expiryDatetime }` instants (ISO-8601
 * with offset) from the calendar date and the shift's wall-clock definition.
 * @param {string} date - "YYYY-MM-DD"
 * @param {{ start: string, end: string, nextDay: boolean }} shift
 * @param {string} [timeZone]
 * @returns {{ effectiveDatetime: string, expiryDatetime: string }}
 */
export const shiftWindowToInstants = (date, shift, timeZone = COPLINE_TIMEZONE) => {
  const end = moment.tz(`${date} ${shift.end}`, timeZone);
  if (shift.nextDay) end.add(1, "day");
  return {
    effectiveDatetime: datetimeToInstant(`${date} ${shift.start}`, timeZone),
    expiryDatetime: end.format(),
  };
};

/**
 * The calendar date an instant falls on, as seen in `timeZone` ("YYYY-MM-DD").
 * Used as the Schedule grid's day-bucket key.
 * @param {string|Date|moment.Moment} instant
 * @param {string} [timeZone]
 * @returns {string}
 */
export const instantToCalendarDate = (instant, timeZone = COPLINE_TIMEZONE) =>
  moment.tz(instant, timeZone).format("YYYY-MM-DD");

/**
 * A short human date for an instant as seen in `timeZone` — e.g. "Friday Jun 5".
 * @param {string|Date|moment.Moment} instant
 * @param {string} [timeZone]
 * @returns {string}
 */
export const instantToReadableDate = (instant, timeZone = COPLINE_TIMEZONE) =>
  moment.tz(instant, timeZone).format("dddd MMM D");

/**
 * A full human date-time for an instant as seen in `timeZone`, with the correct
 * abbreviation — e.g. "June 05 2026, 00:00 EDT".
 * @param {string|Date|moment.Moment} instant
 * @param {string} [timeZone]
 * @returns {string}
 */
export const instantToReadableDateTime = (instant, timeZone = COPLINE_TIMEZONE) =>
  moment.tz(instant, timeZone).format("MMMM DD YYYY, HH:mm z");

/**
 * A shift window's boundary as day + time in `timeZone`, with the correct DST
 * abbreviation — e.g. "Saturday Jul 18 @ 0:00 EDT". Used by the booking modal.
 * @param {string|Date|moment.Moment} instant
 * @param {string} [timeZone]
 * @returns {string}
 */
export const instantToReadableShiftTime = (instant, timeZone = COPLINE_TIMEZONE) =>
  moment.tz(instant, timeZone).format("dddd MMM D @ H:mm z");

/**
 * Whether a shift's start falls on the given Copline calendar day. Replaces the
 * old local-vs-EST split: both the day key and the shift are read in the same
 * zone, so they cannot disagree.
 * @param {{ effectiveDate: string|Date }} shift
 * @param {string} day - "YYYY-MM-DD"
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export const shiftStartsOn = (shift, day, timeZone = COPLINE_TIMEZONE) =>
  instantToCalendarDate(shift.effectiveDate, timeZone) === day;
